import axios from 'axios';

const resolveApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location.hostname.endsWith('syntera.tr')) {
    return `${window.location.origin}/api`;
  }

  return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const CLIENT_SESSION_STORAGE_KEY = 'syntera_client_session_key';

const hashString = (value) => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

const getClientDeviceFingerprint = () => {
  if (typeof window === 'undefined') {
    return 'server-side';
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown-timezone';
  const screenInfo = window.screen
    ? `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`
    : 'unknown-screen';

  const fingerprintSource = [
    navigator.userAgent || 'unknown-ua',
    navigator.platform || 'unknown-platform',
    navigator.language || 'unknown-language',
    navigator.vendor || 'unknown-vendor',
    timezone,
    screenInfo,
    String(window.devicePixelRatio || 1),
    String(navigator.hardwareConcurrency || 0),
    String(navigator.maxTouchPoints || 0),
    String(navigator.deviceMemory || 0),
  ].join('|');

  return hashString(fingerprintSource);
};

const generateClientSessionKey = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const getClientSessionKey = () => {
  if (typeof window === 'undefined') {
    return 'server-side';
  }

  let existing = localStorage.getItem(CLIENT_SESSION_STORAGE_KEY);
  if (!existing) {
    existing = generateClientSessionKey();
    localStorage.setItem(CLIENT_SESSION_STORAGE_KEY, existing);
  }
  return existing;
};

const api = axios.create({
  baseURL: API_BASE_URL,
});

const redirectToLogin = () => {
  if (typeof window === 'undefined' || process.env.NODE_ENV === 'test') {
    return;
  }

  window.location.href = '/login';
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Token'ı requests'e ekle
api.interceptors.request.use((config) => {
  if (config.skipAuth) {
    if (config.headers?.Authorization) {
      delete config.headers.Authorization;
    }
    return config;
  }

  config.headers['X-Client-Session-Key'] = getClientSessionKey();
  config.headers['X-Client-Device-Fingerprint'] = getClientDeviceFingerprint();

  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Token refresh interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (originalRequest?.skipAuth) {
      return Promise.reject(error);
    }

    // Prevent refresh loop on non-401 errors or retry flag already set
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      processQueue(new Error('No refresh token'), null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('is_staff');
      isRefreshing = false;
      redirectToLogin();
      return Promise.reject(error);
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/users/token/refresh/`, {
        refresh: refreshToken,
      });

      const newAccessToken = response.data.access;
      localStorage.setItem('access_token', newAccessToken);
      api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

      processQueue(null, newAccessToken);
      isRefreshing = false;

      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('is_staff');
      isRefreshing = false;
      redirectToLogin();
      return Promise.reject(refreshError);
    }
  }
);

export default api;
